import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input, OnInit,
  Output,
  QueryList, ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import {Offer} from '../../model/offer/offer';
import {OfferStatus} from '../../enum/offer-status';
import {NumberParseService} from '../../services/parser/number-parse.service';
import OfferStatusColors from '../../../assets/config/offer-status-colors.json';
import {ApplicationType} from '../../enum/application-type';
import {MatExpansionPanel} from '@angular/material/expansion';
import {OfferType} from '../../enum/offer-type';
import {Route} from '../../enum/route';
import {Router} from '@angular/router';
import {UserService} from '../../services/user/user.service';
import {OfferService} from '../../services/offer/offer.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {SortOffer} from '../../model/offer/sort-offer';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {selectDetailPageProposal, selectPrivateNotes} from '../../store/proposal/selectors/proposal.selectors';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {selectUserId} from '../../store/user/selectors/user.selectors';
import {BehaviorSubject, map, take} from 'rxjs';
import {PrivateNote} from '../../model/offer/private-note';
import {MatTable} from '@angular/material/table';
import {switchMap} from 'rxjs/operators';
import {OfferReadByUser} from '../../model/offer/offer-read-by-user';


@Component({
    selector: 'app-offer-overview',
    templateUrl: './offer-overview.component.html',
    styleUrls: ['./offer-overview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OfferOverviewComponent implements OnInit, AfterViewInit  {
  @Input() offers: Offer[] = [];
  displayedColumns = ['offer-read', 'status', 'lastTimeEdited', 'creator', 'premium', 'share', 'role', 'action'];

  @Input() insuredAmount;
  @Input() selectedOffer: Offer | null;
  @Input() proposalId : string | null;
  @Output() selectedOfferChange = new EventEmitter<Offer>();
  @ViewChild(MatTable) table: MatTable<any>;
  OfferStatus = OfferStatus;

  OfferType = OfferType;

  proposalUserId$ = this.proposalStore.select(selectDetailPageProposal).pipe(map(proposal => proposal.creator.id));
  activeUserId$ = this.userStore.select(selectUserId);

  private _offersReadByUserIds = new BehaviorSubject<OfferReadByUser[]>([]);
  offersReadByUserIds$ = this._offersReadByUserIds.asObservable();

  NumberParseService = NumberParseService;
  OfferStatusColors = OfferStatusColors;
  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;
  Route = Route;

  @Output() selectedPrivateNote = new EventEmitter<PrivateNote>();
  activeUserId: string | null = null;

  constructor(
    private readonly router: Router,
    public readonly offerService: OfferService,
    public userService: UserService,
    private readonly proposalStore: Store<ProposalAppState>,
    private readonly userStore: Store<UserAppState>
  ) {
  }

  ngOnInit() {
    this.activeUserId$.pipe(
      take(1), // take only the first emitted userId
      switchMap(userId => {
        this.activeUserId = userId;
        return this.offerService.getOfferReadyByUserByProposalAndUser(userId, this.proposalId);
      })
    ).subscribe({
      next: (offers) => this._offersReadByUserIds.next(offers),
    });
  }

  ngAfterViewInit(): void {
    this.table?.renderRows();
  }

  addOfferReadByUser(newOffer: OfferReadByUser) {
    const current = this._offersReadByUserIds.value; // get current array
    this._offersReadByUserIds.next([...current, newOffer]); // emit new array
  }

  // also called by other components to read offer when fast switching between them using buttons
  markOfferAsRead(offer: Offer) {
    this.activeUserId$.pipe(take(1)).subscribe(userId => {
      this.offerService.saveOfferReadyByUser(userId, this.proposalId, offer.offerId)
        .subscribe(savedOffer => {

          const currentOffers = this._offersReadByUserIds.value;

          const exists = currentOffers.some(o =>
            o.offerId === offer.offerId &&
            o.proposalId === this.proposalId &&
            o.userId === userId
          );

          if (!exists) {
            const offerReadByUser = new OfferReadByUser();
            offerReadByUser.offerId = offer.offerId;
            offerReadByUser.proposalId = this.proposalId;
            offerReadByUser.userId = userId;

            this.addOfferReadByUser(offerReadByUser);
          }
        });
    });
  }

  isOfferRead(
    offerId: string,
    proposalId: string,
    offersRead: OfferReadByUser[] | null | undefined,
    creatorId: string
  ): boolean {
    if (!offersRead || !this.activeUserId) return false;
    else if (creatorId === this.activeUserId) return true;

    return offersRead.some(o =>
      o.offerId === offerId &&
      o.proposalId === proposalId &&
      o.userId === this.activeUserId
    );
  }


  selectOffer(offer) {
    this.markOfferAsRead(offer);

    this.selectedOfferChange.emit(offer);
    this.proposalStore.select(selectPrivateNotes).subscribe(privateNotes => {
      const filteredPrivateNotes = privateNotes?.filter(pn => pn?.offerId == offer.offerId);

      if (filteredPrivateNotes?.length > 0) {
        const selectedPrivateNote = filteredPrivateNotes[0];
        this.selectedPrivateNote.emit(selectedPrivateNote);
      } else {
        let offerPrivateNote = new PrivateNote();
        offerPrivateNote.offerId = offer.offerId;
        this.selectedPrivateNote.emit(offerPrivateNote);
      }
    });
  }

  getRole(offer: Offer) {
    return offer.isLeading ? ApplicationType.LEAD_INSURER : ApplicationType.FOLLOWING_INSURER;
  }

  navigateToUserProfile(userId) {
    this.router.navigate([Route.USER_PAGE, userId]).then();

  }

  drop(event: CdkDragDrop<Offer[]>) {
    moveItemInArray(this.offers, event.previousIndex, event.currentIndex);
    this.table?.renderRows();

    const sortedOffers: SortOffer[] = this.offers.map((offer, index) => ({
      proposalId: offer.proposalId,
      offerId: offer.offerId,
      orderNumber: index
    }));

    for (const sortedOffer of sortedOffers) {
      this.offerService.saveOrUpdateSortOrder(sortedOffer)?.subscribe();
    }
  }
}
